import { forwardRef } from 'react';
import { CircleAlert as AlertCircle } from 'lucide-react';
import {
  computeTotals,
  fmtMoney,
  fmtQty,
  formatReportDate,
  lineValues,
  type StoreReport,
} from '@/lib/consignmentStoreReport';

interface Props {
  report: StoreReport;
}

export const ConsignmentStoreReportDocument = forwardRef<HTMLDivElement, Props>(function ConsignmentStoreReportDocument(
  { report },
  ref,
) {
  const totals = computeTotals(report);

  return (
    <div ref={ref} dir="rtl" className="bg-white text-gray-900 p-8 min-w-[720px]">
      <header className="flex items-start justify-between gap-6 pb-6 border-b-2 border-gray-900">
        <div className="flex items-center gap-4">
          {report.logo_url && (
            <img
              data-pdf-keep
              src={report.logo_url}
              alt=""
              crossOrigin="anonymous"
              className="h-14 w-14 object-contain"
            />
          )}
          <div>
            <p className="text-xl font-bold leading-tight">{report.company_name}</p>
            <p className="text-sm text-gray-500 mt-1">דוח יתרות מלאי קונסיגנציה</p>
          </div>
        </div>
        <dl className="text-sm grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
          <dt className="text-gray-500">חנות</dt>
          <dd className="font-semibold">{report.client_name}</dd>
          <dt className="text-gray-500">מחסן</dt>
          <dd className="font-semibold">{report.warehouse_name ?? '—'}</dd>
          <dt className="text-gray-500">נכון לתאריך</dt>
          <dd className="font-semibold">{formatReportDate(report.as_of)}</dd>
        </dl>
      </header>

      {report.lines.length === 0 ? (
        <p className="py-16 text-center text-sm text-gray-500">אין יתרות מלאי לתאריך זה</p>
      ) : (
        <table className="w-full text-sm border-collapse mt-6">
          <thead>
            <tr className="border-b border-gray-300 text-gray-600">
              <th className="py-2 px-2 text-right font-semibold">מק״ט</th>
              <th className="py-2 px-2 text-right font-semibold">שם מוצר</th>
              <th className="py-2 px-2 text-center font-semibold">כמות</th>
              <th className="py-2 px-2 text-left font-semibold">מחיר יחידה</th>
              <th className="py-2 px-2 text-left font-semibold">שווי לפני מע״מ</th>
              <th className="py-2 px-2 text-left font-semibold">שווי כולל מע״מ</th>
            </tr>
          </thead>
          <tbody>
            {report.lines.map((l) => {
              const v = lineValues(l, report.vat_rate);
              return (
                <tr key={l.product_id} className="border-b border-gray-100">
                  <td className="py-2 px-2 font-mono text-xs text-gray-600">{l.sku ?? '—'}</td>
                  <td className="py-2 px-2">{l.product_name}</td>
                  <td className="py-2 px-2 text-center font-semibold">{fmtQty(l.qty)}</td>
                  {v ? (
                    <>
                      <td className="py-2 px-2 text-left">{fmtMoney(l.unit_price!)}</td>
                      <td className="py-2 px-2 text-left">{fmtMoney(v.beforeVat)}</td>
                      <td className="py-2 px-2 text-left">{fmtMoney(v.inclVat)}</td>
                    </>
                  ) : (
                    <td colSpan={3} className="py-2 px-2 text-left text-amber-700 font-medium">ללא מחיר</td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-900 font-bold">
              <td className="py-2.5 px-2" colSpan={2}>סה״כ ({report.lines.length} פריטים)</td>
              <td className="py-2.5 px-2 text-center">{fmtQty(totals.qty)}</td>
              <td />
              <td className="py-2.5 px-2 text-left">{fmtMoney(totals.beforeVat)}</td>
              <td className="py-2.5 px-2 text-left">{fmtMoney(totals.inclVat)}</td>
            </tr>
          </tfoot>
        </table>
      )}

      {report.lines.length > 0 && (
        <div className="mt-6 flex justify-end">
          <dl className="w-72 text-sm grid grid-cols-2 gap-y-1.5 bg-gray-50 rounded-lg p-4">
            <dt className="text-gray-600">שווי לפני מע״מ</dt>
            <dd className="text-left font-semibold">{fmtMoney(totals.beforeVat)}</dd>
            <dt className="text-gray-600">מע״מ ({report.vat_rate}%)</dt>
            <dd className="text-left font-semibold">{fmtMoney(totals.vat)}</dd>
            <dt className="text-gray-900 font-bold pt-1.5 border-t border-gray-200">שווי כולל מע״מ</dt>
            <dd className="text-left font-bold pt-1.5 border-t border-gray-200">{fmtMoney(totals.inclVat)}</dd>
          </dl>
        </div>
      )}

      {totals.unpricedCount > 0 && (
        <p className="mt-4 flex items-center gap-2 text-xs text-amber-700">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {totals.unpricedCount} פריטים ללא מחיר במחירון אינם כלולים בשווי
        </p>
      )}

      <p className="mt-8 text-[11px] text-gray-400">
        המחירים לפי מחירון החנות · יתרה לסוף היום
      </p>
    </div>
  );
});
